import { useCallback } from 'react';

import {
  useCorporateSubscriptionCreate,
  useCorporateSubscriptionDeactivate,
  useCorporateSubscriptionReactivate,
  useCorporateSubscriptionUpdate,
  useGroupAdminCreate,
  useGroupAdminRemove,
  useGroupAdminUpdate,
  useGroupAdminWelcomeEmail,
} from '@hooks';
import { ACCESS_ANNUAL, ACCESS_PRO } from '@utils/constants';
import {
  getRecordId,
  modalActions,
  modalButtonTexts,
  modalHeadlines,
} from '@utils/corporateSubscriptionHelpers';

import { GroupAdmin, GroupUser } from '../../types';
import {
  FormActionType,
  FormButtonType,
  FormValues,
  UserOperationType,
  UserType,
} from '../../types';

export const useUserForm = (userType: UserType, operation: UserOperationType) => {
  const { mutateAsync: createCorporateSubscription } = useCorporateSubscriptionCreate();
  const { mutateAsync: updateCorporateSubscription } = useCorporateSubscriptionUpdate();
  const { mutateAsync: deactivateCorporateSubscription } = useCorporateSubscriptionDeactivate();
  const { mutateAsync: reactivateCorporateSubscription } = useCorporateSubscriptionReactivate();
  const { mutateAsync: createGroupAdmin } = useGroupAdminCreate();
  const { mutateAsync: updateGroupAdmin } = useGroupAdminUpdate();
  const { mutateAsync: removeGroupAdmin } = useGroupAdminRemove();
  const { mutateAsync: sendGroupAdminWelcomeEmail } = useGroupAdminWelcomeEmail();

  const getFormHeadline = useCallback(() => {
    return modalHeadlines[userType]?.[operation] ?? '';
  }, [userType, operation]);

  const getButtonText = useCallback(
    (buttonType: FormButtonType): string => {
      return modalButtonTexts[userType]?.[operation]?.[buttonType] ?? '';
    },
    [userType, operation]
  );

  const getActionType = useCallback(
    (buttonType: FormButtonType): FormActionType | null => {
      return modalActions[userType]?.[operation]?.[buttonType] ?? null;
    },
    [userType, operation]
  );

  const handleSubmit = useCallback(
    async (values: FormValues, user: GroupAdmin | GroupUser, buttonType: FormButtonType) => {
      const { name, email, pro } = values;
      const id = getRecordId(user);
      const actionType = getActionType(buttonType);
      const access_level = pro ? ACCESS_PRO : ACCESS_ANNUAL;

      if (!actionType) return;

      switch (actionType) {
        case FormActionType.CREATE_ADMIN:
          await createGroupAdmin({
            admin: { email, name },
          });
          break;
        case FormActionType.UPDATE_ADMIN:
          await updateGroupAdmin({
            id,
            admin: { email, name },
          });
          break;
        case FormActionType.REMOVE_ADMIN:
          await removeGroupAdmin(id);
          break;
        case FormActionType.CREATE_USER:
          await createCorporateSubscription({
            corporate_subscription: {
              access_level,
            },
            user: {
              email,
              name,
            },
          });
          break;
        case FormActionType.UPDATE_USER:
          await updateCorporateSubscription({
            id,
            corporate_subscription: {
              access_level,
            },
            user: {
              email,
              name,
            },
          });
          break;
        case FormActionType.DEACTIVATE_USER:
          await deactivateCorporateSubscription(id);
          break;
        case FormActionType.REACTIVATE_USER:
          await reactivateCorporateSubscription({
            id,
            corporate_subscription: {
              access_level,
            },
          });
          break;
        default:
          throw new Error('Unsupported action type');
      }
    },
    [
      createCorporateSubscription,
      createGroupAdmin,
      deactivateCorporateSubscription,
      getActionType,
      reactivateCorporateSubscription,
      removeGroupAdmin,
      updateCorporateSubscription,
      updateGroupAdmin,
    ]
  );

  return {
    getActionType,
    getButtonText,
    getFormHeadline,
    handleSubmit,
    sendGroupAdminWelcomeEmail,
  };
};
